import {useMemo, useRef} from 'react'
import PropTypes from 'prop-types'

import {REST_LINES} from './lifeStoryContent'

/** Picks a rest headline at random, avoiding an immediate repeat across mounts. */
let lastRestLineIndex = -1

const pickRestLineIndex = () => {
  let index

  do
    index = Math.floor(Math.random() * REST_LINES.length)
  while (index === lastRestLineIndex && REST_LINES.length > 1)

  lastRestLineIndex = index

  return index
}

/**
 * The pause offered after three stories in one sitting (mock's chapter rest):
 * celebrate the progress, then continue, switch chapters, or stop for now.
 * Continuing is always possible — a chapter never runs dry, because its open
 * "anything else…" question can take one more story.
 *
 * The copy never mentions how many stories were added: an "anything else…"
 * answer brings the rest screen up after fewer than a full sitting, and
 * claiming "three stories" after one would ring false (TK-2983 AC 4.9).
 */
const RestScreen = ({chapterName, continueChapter, beginNextChapter, takeBreak}) => {
  const lineIndexRef = useRef(null)

  if (lineIndexRef.current === null)
    lineIndexRef.current = pickRestLineIndex()

  const headline = useMemo(() => {
    const parts = REST_LINES[lineIndexRef.current].split('{c}')

    if (parts.length === 1) return parts[0]

    return (
      <span>
        {parts[0]}
        <em>{chapterName}</em>
        {parts[1]}
      </span>
    )
  }, [chapterName])

  return (
    <div className='ls-rest'>
      <div aria-hidden='true' className='ls-rest-dots'>&middot; &middot; &middot;</div>
      <div className='ls-rest-kicker'>A good place to rest</div>
      <h1>{headline}</h1>
      <p className='ls-rest-sub'>
        Already something your family will treasure. Keep going if the memories are flowing,
        or take a break for now. Everything is saved.
      </p>
      <div className='ls-rest-actions'>
        <button className='ls-btn ls-btn-primary' onClick={continueChapter}>Continue this chapter</button>
        <button className='ls-btn ls-btn-outline' onClick={beginNextChapter}>Begin the next chapter</button>
        <button className='ls-link-quiet' onClick={takeBreak}>Take a break &mdash; I&rsquo;m done for now</button>
      </div>
    </div>
  )
}

RestScreen.propTypes = {
  beginNextChapter: PropTypes.func,
  chapterName: PropTypes.string,
  continueChapter: PropTypes.func,
  takeBreak: PropTypes.func
}

export default RestScreen
